import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { eur, fmtDate, hrs, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { StatusBadge } from "@/components/StatusBadge";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { EmployeeDocs, docStatus } from "@/components/EmployeeDocs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ChevronDown, Pencil, KeyRound, FileText, Clock, Lock, Trash2, CalendarPlus, Thermometer, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useIsManager } from "@/lib/role";
import { useAuth } from "@/context/AuthContext";

const avatarColor = (name) => {
  const colors = ["bg-rose-600", "bg-amber-600", "bg-emerald-600", "bg-sky-600", "bg-indigo-600", "bg-fuchsia-600", "bg-teal-600", "bg-orange-600"];
  let h = 0; for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[h];
};
const isAnnual = (t) => (t || "").trim().toLowerCase() === "annual leave";
const isSick = (t) => (t || "").toLowerCase().includes("sick");
const statusTone = (s) => ({ approved: "bg-emerald-100 text-emerald-700", valid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-800", rejected: "bg-rose-100 text-rose-700", cancelled: "bg-zinc-100 text-zinc-500" }[(s || "").toLowerCase()] || "bg-zinc-100 text-zinc-600");

function Stat({ label, value, sub, testId, tone }) {
  return (
    <div data-testid={testId} className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-2xl font-heading font-black mt-1 ${tone || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}
function Field({ label, value }) {
  return <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-50 text-sm"><span className="text-zinc-500">{label}</span><span className="font-medium text-right break-words">{value || "—"}</span></div>;
}
function Card({ title, children }) {
  return <div className="bg-white border border-zinc-200 rounded-md p-4"><h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{title}</h3>{children}</div>;
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const mgr = useIsManager();
  const [absFilter, setAbsFilter] = useState("all");
  const { user: me } = useAuth();
  const isSuper = me?.role === "amden_super" || !!me?._impersonation?.active;
  const [adjOpen, setAdjOpen] = useState(false);
  const canEditLeave = isSuper || me?.role === "kqm_admin";
  const [balOpen, setBalOpen] = useState(false);
  const [balForm, setBalForm] = useState({ entitlement: "", remaining: "", reason: "" });
  const [adjForm, setAdjForm] = useState({ leave_type: "Annual Leave", days: "", date: "", reason: "" });
  const [lateOpen, setLateOpen] = useState(false);
  const [lateEdit, setLateEdit] = useState(null);
  const [lateForm, setLateForm] = useState({ date: "", minutes: "", note: "" });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: "Annual Leave", start_date: "", end_date: "", half_day: false, note: "", approve: true });
  const [sickOpen, setSickOpen] = useState(false);
  const [sickForm, setSickForm] = useState({ first_day: "", expected_return: "", actual_return: "", certified: false, pay_type: "unpaid" });

  const load = useCallback(() => {
    api.get(`/employees/${id}/profile`, { params: { year } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load profile"));
  }, [id, year]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/leave-types").then((r) => setLeaveTypes(r.data)).catch(() => {}); }, []);

  const openLate = (row) => {
    if (row) { setLateEdit(row); setLateForm({ date: row.date || "", minutes: String(row.minutes ?? ""), note: row.note || "" }); }
    else { setLateEdit(null); setLateForm({ date: new Date().toISOString().slice(0, 10), minutes: "", note: "" }); }
    setLateOpen(true);
  };
  const saveLate = async () => {
    if (!lateForm.date) { toast.error("Date is required"); return; }
    try {
      if (lateEdit) await api.put(`/lateness/${lateEdit.id}`, { date: lateForm.date, minutes: Number(lateForm.minutes || 0), note: lateForm.note });
      else await api.post(`/employees/${id}/lateness`, { date: lateForm.date, minutes: Number(lateForm.minutes || 0), note: lateForm.note });
      toast.success(lateEdit ? "Lateness updated" : "Lateness logged");
      setLateOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delLate = async (lid) => { try { await api.delete(`/lateness/${lid}`); toast.success("Lateness removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openLeave = () => { setLeaveForm({ leave_type: leaveTypes[0]?.name || "Annual Leave", start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10), half_day: false, note: "", approve: true }); setLeaveOpen(true); };
  const saveLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error("Start and end dates are required"); return; }
    try {
      await api.post("/leave-requests", { employee_id: id, leave_type: leaveForm.leave_type, start_date: leaveForm.start_date, end_date: leaveForm.end_date, half_day: leaveForm.half_day, note: leaveForm.note, auto_approve: leaveForm.approve });
      toast.success(leaveForm.approve ? "Leave logged & approved" : "Leave logged (pending)");
      setLeaveOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openSick = () => { setSickForm({ first_day: new Date().toISOString().slice(0, 10), expected_return: "", actual_return: "", certified: false, pay_type: "unpaid" }); setSickOpen(true); };
  const saveSick = async () => {
    if (!sickForm.first_day) { toast.error("First day is required"); return; }
    try {
      await api.post("/sick-leave", { employee_id: id, first_day: sickForm.first_day, expected_return: sickForm.expected_return || null, actual_return: sickForm.actual_return || null, certified: sickForm.certified, pay_type: sickForm.pay_type });
      toast.success("Sickness logged"); setSickOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveLeave = async (lrId) => { try { await api.post(`/leave-requests/${lrId}/approve`); toast.success("Leave approved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const doReject = async () => { try { await api.post(`/leave-requests/${rejectTarget}/reject`, { reject_reason: rejectReason }); toast.success("Leave rejected"); setRejectTarget(null); setRejectReason(""); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delSick = async (sid) => { if (!window.confirm("Remove this sickness record?")) return; try { await api.delete(`/sick-leave/${sid}`); toast.success("Sickness removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delLeave = async (lid) => { if (!window.confirm("Remove this leave record? Any approved annual leave days are restored to the balance and removed from timesheets. It can be restored from Deleted Items for 30 days.")) return; try { await api.delete(`/leave-requests/${lid}`); toast.success("Leave removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delAdj = async (aid) => { if (!window.confirm("Remove this adjustment?")) return; try { await api.delete(`/leave-adjustments/${aid}`); toast.success("Adjustment removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openAdj = () => { setAdjForm({ leave_type: leaveTypes[0]?.name || "Annual Leave", days: "", date: new Date().toISOString().slice(0, 10), reason: "" }); setAdjOpen(true); };
  const openBal = () => { const b = data?.leave_balance || {}; setBalForm({ entitlement: b.entitlement ?? "", remaining: b.remaining ?? "", reason: "", _ent: b.entitlement, _rem: b.remaining }); setBalOpen(true); };
  const saveBal = async () => {
    const body = { reason: balForm.reason };
    if (String(balForm.entitlement) !== String(balForm._ent ?? "")) body.entitlement = Number(balForm.entitlement);
    if (String(balForm.remaining) !== String(balForm._rem ?? "")) body.remaining = Number(balForm.remaining);
    try { await api.put(`/employees/${id}/leave-balance`, body); toast.success("Leave balance updated"); setBalOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveAdj = async () => {
    try { await api.post(`/employees/${id}/leave-adjustments`, { ...adjForm, days: Number(adjForm.days) }); toast.success("Leave taken adjusted"); setAdjOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <AdminLayout nav={adminNav} title="Employee"><div className="text-zinc-400">Loading…</div></AdminLayout>;

  const emp = data.employee;
  const name = `${emp.first_name || ""} ${emp.surname || ""}`.trim();
  const trackLateness = data.settings?.track_lateness;
  const bal = data.leave_balance || {};
  const leave = data.leave_requests || [];
  const sick = data.sick_leave || [];
  const late = data.lateness || [];
  const adjs = data.leave_adjustments || [];
  const adjSum = (pred) => adjs.filter((a) => pred(a.leave_type)).reduce((s, a) => s + (a.days || 0), 0);

  const annualReqs = leave.filter((l) => isAnnual(l.leave_type));
  const otherReqs = leave.filter((l) => !isAnnual(l.leave_type) && !isSick(l.leave_type));
  const sickReqs = leave.filter((l) => isSick(l.leave_type));
  const sickCount = sick.length + sickReqs.length;
  const sickDays = sick.reduce((a, s) => a + (s.days || 0), 0) + sickReqs.reduce((a, s) => a + (s.working_days || 0), 0) + adjSum(isSick);
  const otherDays = otherReqs.reduce((a, s) => a + (s.working_days || 0), 0) + adjSum((t) => !isAnnual(t) && !isSick(t));
  const lateMinutes = late.reduce((a, l) => a + (l.minutes || 0), 0);

  // unified history
  const hist = [];
  leave.forEach((l) => hist.push({
    key: `l-${l.id}`, lrId: l.id, cat: isAnnual(l.leave_type) ? "annual" : isSick(l.leave_type) ? "sickness" : "other",
    type: l.leave_type, start: l.start_date, end: l.end_date, sort: l.created_at || l.start_date,
    duration: `${l.working_days ?? "—"} day${(l.working_days === 1) ? "" : "s"}${l.half_day ? " (½)" : ""}`,
    status: l.status, by: l.approved_by, at: l.approved_at, note: l.note,
  }));
  sick.forEach((s) => hist.push({
    key: `s-${s.id}`, sickId: s.id, cat: "sickness", type: "Sickness", start: s.first_day, end: s.actual_return || s.expected_return,
    sort: s.created_at || s.first_day, duration: `${s.days || 1} day${s.days === 1 ? "" : "s"}${s.certified ? " · certified" : ""}`,
    status: s.actual_return ? "returned" : "ongoing", by: s.created_by, at: s.created_at, note: s.pay_type,
  }));
  late.forEach((l) => hist.push({
    key: `t-${l.id}`, cat: "lateness", type: "Lateness", start: l.date, end: null, sort: l.date,
    duration: `${l.minutes || 0} min late`, status: null, by: l.created_by, at: l.created_at, note: l.note, raw: l,
  }));
  adjs.forEach((a) => hist.push({
    key: `a-${a.id}`, adjId: a.id, cat: isAnnual(a.leave_type) ? "annual" : isSick(a.leave_type) ? "sickness" : "other",
    type: `${a.leave_type} adjustment`, start: a.date, end: null, sort: a.created_at,
    duration: `${a.days > 0 ? "+" : ""}${a.days} day${Math.abs(a.days) === 1 ? "" : "s"} taken`, status: "adjusted", by: a.created_by, at: a.created_at, note: a.reason,
  }));
  const filteredHist = hist.filter((h) => absFilter === "all" || h.cat === absFilter).sort((a, b) => (b.sort || "").localeCompare(a.sort || ""));

  const chips = [["all", "All"], ["annual", "Annual"], ["sickness", "Sickness"], ["other", "Other"]];
  if (trackLateness) chips.push(["lateness", "Lateness"]);

  const payLabel = emp.pay_type === "salaried" ? `${eur(emp.annual_salary)} / yr` : `${eur(emp.pay_rate)} /hr`;
  const complianceNote = emp.compliance && !emp.compliance.compliant ? emp.compliance.missing?.slice(0, 3).join(", ") : null;

  return (
    <AdminLayout nav={adminNav} title={name}>
      <Link to="/admin/employees" data-testid="emp-profile-back" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-slate-900 mb-3"><ChevronLeft className="h-4 w-4" />Back to Employees</Link>

      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-md p-5 mb-5 flex flex-wrap items-center gap-4" data-testid="emp-profile-header">
        <div className={`h-16 w-16 rounded-full ${avatarColor(name)} text-white flex items-center justify-center font-heading font-black text-xl shrink-0`}>
          {(emp.first_name?.[0] || "") + (emp.surname?.[0] || "")}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading font-black text-2xl text-slate-900 truncate">{name}</h1>
            <StatusBadge status={emp.active ? "active" : (emp.status || "inactive")} />
            {emp.is_admin && <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Admin</span>}
          </div>
          <div className="text-sm text-zinc-500 mt-0.5">{emp.grade || emp.job_title}{emp.employment_type ? ` · ${emp.employment_type}` : ""}{emp.employee_number ? ` · #${emp.employee_number}` : ""}</div>
          <div className="flex items-center gap-2 mt-2">
            <ComplianceBadge c={emp.compliance} />
            {complianceNote && <span className="text-xs text-amber-700">Outstanding: {complianceNote}</span>}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {data.login?.locked && !mgr && <span data-testid="emp-profile-locked" className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-full px-2.5 py-1"><Lock className="h-3 w-3" />Login locked</span>}
          {!mgr && <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="emp-quick-actions" className="bg-slate-900 text-white">Quick actions <ChevronDown className="h-4 w-4 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem data-testid="qa-edit" onClick={() => navigate(`/admin/employees?edit=${id}`)}><Pencil className="h-4 w-4 mr-2" />Edit details</DropdownMenuItem>
              <DropdownMenuItem data-testid="qa-login" onClick={() => navigate(`/admin/employees?edit=${id}`)}><KeyRound className="h-4 w-4 mr-2" />{data.login?.has_login ? "Reset / unlock login" : "Create login"}</DropdownMenuItem>
              <DropdownMenuItem data-testid="qa-doc" onClick={() => setTab("documents")}><FileText className="h-4 w-4 mr-2" />Add document</DropdownMenuItem>
              <DropdownMenuItem data-testid="qa-leave" onClick={openLeave}><CalendarPlus className="h-4 w-4 mr-2" />Log leave</DropdownMenuItem>
              <DropdownMenuItem data-testid="qa-sick" onClick={openSick}><Thermometer className="h-4 w-4 mr-2" />Log sickness</DropdownMenuItem>
              {trackLateness && <DropdownMenuItem data-testid="qa-late" onClick={() => openLate(null)}><Clock className="h-4 w-4 mr-2" />Log lateness</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-absence" value="absence">Absence & Leave</TabsTrigger>
          {!mgr && <TabsTrigger data-testid="tab-overtime" value="overtime">Overtime</TabsTrigger>}
          <TabsTrigger data-testid="tab-employment" value="employment">Employment</TabsTrigger>
          <TabsTrigger data-testid="tab-documents" value="documents">Documents</TabsTrigger>
          <TabsTrigger data-testid="tab-personal" value="personal">Personal & Emergencies</TabsTrigger>
        </TabsList>

        {/* Year selector shared by absence/overtime */}
        {(tab === "absence" || tab === "overtime") && (
          <div className="flex items-center gap-2 mb-4" data-testid="emp-year-nav">
            <Button variant="outline" size="sm" className="h-8" onClick={() => setYear((y) => y - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="num font-semibold w-14 text-center">{year}</span>
            <Button variant="outline" size="sm" className="h-8" disabled={year >= nowYear} onClick={() => setYear((y) => y + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}

        <TabsContent value="overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat testId="ov-annual" label="Annual leave left" value={`${hrs(bal.remaining ?? 0)}`} sub={`of ${hrs((bal.entitlement ?? 0) + (bal.carry_forward ?? 0))} days`} tone="text-emerald-600" />
            <Stat testId="ov-sick" label="Sickness this year" value={sickCount} sub={`${hrs(sickDays)} days`} tone="text-amber-600" />
            {!mgr && <Stat testId="ov-ot" label="Overtime this year" value={`${hrs(data.overtime?.total_hours ?? 0)}h`} sub={`${data.overtime?.weeks?.length || 0} weeks`} />}
            <Stat testId="ov-compliance" label="Compliance" value={`${emp.compliance?.pct ?? 0}%`} sub={emp.compliance?.compliant ? "All good" : (complianceNote || "Review")} tone={emp.compliance?.compliant ? "text-emerald-600" : "text-amber-600"} />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Card title="Identity">
              <Field label="Full name" value={name} />
              <Field label="Employee no." value={emp.employee_number} />
              <Field label="Role / grade" value={emp.grade || emp.job_title} />
              <Field label="Employment type" value={emp.employment_type} />
              <Field label="Usual weekly hours" value={emp.usual_weekly_hours ? hrs(emp.usual_weekly_hours) : "Full-time (standard day)"} />
              <Field label="Status" value={emp.active ? "Active" : "Inactive"} />
            </Card>
            <Card title="Contact">
              <Field label="Email" value={emp.email} />
              <Field label="Mobile" value={emp.mobile} />
              <Field label="Login" value={data.login?.has_login ? (data.login.locked ? "Locked" : "Active") : "No login"} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="absence">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat testId="abs-annual" label="Annual leave" value={`${hrs(bal.taken ?? 0)} taken`} sub={`${hrs(bal.remaining ?? 0)} remaining`} tone="text-emerald-600" />
            <Stat testId="abs-sick" label="Sickness" value={`${sickCount} logged`} sub={`${hrs(sickDays)} days total`} tone="text-amber-600" />
            <Stat testId="abs-other" label="Other leave" value={`${otherReqs.length} logged`} sub={`${hrs(otherDays)} days total`} />
            {trackLateness && <Stat testId="abs-late" label="Lateness" value={`${late.length} logged`} sub={`${lateMinutes} min total`} tone="text-rose-600" />}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            {canEditLeave && <Button data-testid="edit-leave-balance-btn" size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-800" onClick={openBal}>Edit entitlement / leave left</Button>}
            {canEditLeave && <Button data-testid="adjust-leave-btn" size="sm" variant="outline" className="h-8 mr-2 border-amber-300 text-amber-800" onClick={openAdj}>Adjust leave taken</Button>}
            {chips.map(([v, l]) => (
              <button key={v} type="button" data-testid={`abs-chip-${v}`} onClick={() => setAbsFilter(v)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${absFilter === v ? "bg-slate-900 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>{l}</button>
            ))}
          </div>

          <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid="abs-history">
            {filteredHist.length === 0 && <div data-testid="abs-empty" className="p-6 text-center text-zinc-400 text-sm">No {absFilter === "all" ? "absence" : absFilter} records in {year}.</div>}
            {filteredHist.map((h) => (
              <div key={h.key} data-testid={`abs-item-${h.key}`} className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-900">{h.type}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{h.cat}</span>
                    {h.status && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusTone(h.status)}`}>{h.status}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">{fmtDate(h.start)}{h.end && h.end !== h.start ? ` → ${fmtDate(h.end)}` : ""} · {h.duration}</div>
                  {h.note && <div className="text-xs text-zinc-400 mt-0.5 truncate">“{h.note}”</div>}
                  {(h.by || h.at) && <div className="text-[11px] text-zinc-400 mt-0.5">{h.by ? `by ${h.by}` : ""}{h.at ? ` · ${fmtDate(h.at)}` : ""}</div>}
                </div>
                {h.cat === "lateness" && !mgr && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`late-edit-${h.raw.id}`} onClick={() => openLate(h.raw)} className="text-zinc-400 hover:text-slate-900"><Pencil className="h-4 w-4" /></button>
                    <button type="button" data-testid={`late-del-${h.raw.id}`} onClick={() => delLate(h.raw.id)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
                {h.lrId && h.status === "pending" && !mgr && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`leave-approve-${h.lrId}`} title="Approve" onClick={() => approveLeave(h.lrId)} className="text-emerald-500 hover:text-emerald-700"><Check className="h-4 w-4" /></button>
                    <button type="button" data-testid={`leave-reject-${h.lrId}`} title="Reject" onClick={() => { setRejectTarget(h.lrId); setRejectReason(""); }} className="text-rose-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                  </div>
                )}
                {h.sickId && !mgr && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" data-testid={`sick-del-${h.sickId}`} title="Remove" onClick={() => delSick(h.sickId)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
                {canEditLeave && h.lrId && <button type="button" data-testid={`leave-del-${h.lrId}`} title="Remove leave record" onClick={() => delLeave(h.lrId)} className="text-rose-400 hover:text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>}
                {canEditLeave && h.adjId && <button type="button" data-testid={`adj-del-${h.adjId}`} title="Remove adjustment" onClick={() => delAdj(h.adjId)} className="text-rose-400 hover:text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="overtime">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat testId="ot-total" label={`Overtime ${year}`} value={`${hrs(data.overtime?.total_hours ?? 0)}h`} sub={`${data.overtime?.weeks?.length || 0} weeks with OT`} />
          </div>
          <div className="bg-white border border-zinc-200 rounded-md" data-testid="ot-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200"><th className="p-3">Week starting</th><th className="p-3 text-right">Overtime hours</th></tr></thead>
              <tbody>
                {(data.overtime?.weeks || []).length === 0 && <tr><td colSpan={2} data-testid="ot-empty" className="p-6 text-center text-zinc-400">No overtime recorded in {year}.</td></tr>}
                {(data.overtime?.weeks || []).map((w) => (
                  <tr key={w.week_start} className="border-b border-zinc-50"><td className="p-3 num">{fmtDate(w.week_start)}</td><td className="p-3 num text-right font-medium">{hrs(w.overtime_hours)}h</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-400 mt-2">Read-only — sourced from approved timesheets.</p>
        </TabsContent>

        <TabsContent value="employment">
          <div className="grid md:grid-cols-2 gap-5">
            {mgr ? (
              <Card title="Employment">
                <Field label="Role / grade" value={emp.grade || emp.job_title} />
                <Field label="Employment type" value={emp.employment_type} />
                <Field label="Start date" value={emp.start_date ? fmtDate(emp.start_date) : null} />
                <Field label="Status" value={emp.active ? "Active" : "Inactive"} />
              </Card>
            ) : <>
            <Card title="Pay & terms">
              <Field label="Pay type" value={emp.pay_type === "salaried" ? "Salaried" : "Hourly"} />
              <Field label={emp.pay_type === "salaried" ? "Annual salary" : "Standard rate"} value={payLabel} />
              {emp.pay_type !== "salaried" && <Field label="Overtime rate" value={`${eur(emp.overtime_rate)} /hr`} />}
              <Field label="PRSI class" value={emp.prsi_class} />
              <Field label="Employment type" value={emp.employment_type} />
              <Field label="Start date" value={emp.start_date ? fmtDate(emp.start_date) : null} />
              <Field label="Status" value={emp.active ? "Active" : "Inactive"} />
            </Card>
            <Card title="Rate history">
              {(data.rates || []).length === 0 && <div className="text-sm text-zinc-400">No rate history recorded.</div>}
              {(data.rates || []).slice().sort((a, b) => (b.effective_start || "").localeCompare(a.effective_start || "")).map((r) => (
                <div key={r.id} className="flex justify-between py-1.5 border-b border-zinc-50 text-sm">
                  <span className="text-zinc-600 capitalize">{r.rate_type}</span>
                  <span className="num font-medium">{eur(r.value)} <span className="text-xs text-zinc-400">from {fmtDate(r.effective_start)}{r.effective_end ? ` to ${fmtDate(r.effective_end)}` : ""}</span></span>
                </div>
              ))}
            </Card>
            </>}
          </div>
          {!mgr && <p className="text-xs text-zinc-400 mt-3">Edit pay and terms via <button type="button" className="text-amber-700 underline" onClick={() => navigate(`/admin/employees?edit=${id}`)}>Quick actions → Edit details</button>.</p>}
        </TabsContent>

        <TabsContent value="documents">
          <Card title="Certs, training & documents">
            <EmployeeDocs eid={id} onChange={load} readOnly={mgr} />
          </Card>
        </TabsContent>

        <TabsContent value="personal">
          <div className="grid md:grid-cols-2 gap-5">
            <Card title="Contact details">
              <Field label="Email" value={emp.email} />
              <Field label="Mobile" value={emp.mobile} />
              <Field label="Address" value={emp.address} />
              {!mgr && <Field label="PPS number" value={emp.pps_number} />}
            </Card>
            <Card title="Emergency / next of kin">
              <Field label="Next of kin" value={emp.next_of_kin} />
              <Field label="Contact phone" value={emp.next_of_kin_phone} />
            </Card>
          </div>
          {!mgr && <p className="text-xs text-zinc-400 mt-3">Edit via <button type="button" className="text-amber-700 underline" onClick={() => navigate(`/admin/employees?edit=${id}`)}>Quick actions → Edit details</button>.</p>}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject leave request</DialogTitle>
            <DialogDescription>Add a reason — the employee will see this on their request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Reason for rejection" />
            <Button data-testid="reject-confirm" onClick={doReject} className="w-full bg-rose-600 text-white">Reject request</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Log leave for {name}</DialogTitle>
            <DialogDescription>Record (and optionally approve) leave on this employee's behalf.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Leave type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                <SelectTrigger data-testid="leave-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{leaveTypes.map((t) => <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>From</Label><Input data-testid="leave-start" type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })} className="mt-1" /></div>
              <div><Label>To</Label><Input data-testid="leave-end" type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="flex items-center justify-between"><Label>Half day</Label><Switch data-testid="leave-half" checked={leaveForm.half_day} onCheckedChange={(v) => setLeaveForm({ ...leaveForm, half_day: v })} /></div>
            <div><Label>Note (optional)</Label><Textarea data-testid="leave-note" value={leaveForm.note} onChange={(e) => setLeaveForm({ ...leaveForm, note: e.target.value })} className="mt-1" rows={2} /></div>
            <div className="flex items-center justify-between"><Label>Approve immediately</Label><Switch data-testid="leave-approve" checked={leaveForm.approve} onCheckedChange={(v) => setLeaveForm({ ...leaveForm, approve: v })} /></div>
            <Button data-testid="leave-save" onClick={saveLeave} className="w-full bg-slate-900 text-white">{leaveForm.approve ? "Log & approve" : "Log as pending"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sickOpen} onOpenChange={setSickOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Log sickness for {name}</DialogTitle>
            <DialogDescription>Record a sickness absence. Leave the return date blank if ongoing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First day</Label><Input data-testid="sick-first" type="date" value={sickForm.first_day} onChange={(e) => setSickForm({ ...sickForm, first_day: e.target.value })} className="mt-1" /></div>
              <div><Label>Expected return</Label><Input data-testid="sick-expected" type="date" value={sickForm.expected_return} onChange={(e) => setSickForm({ ...sickForm, expected_return: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Actual return (if back)</Label><Input data-testid="sick-actual" type="date" value={sickForm.actual_return} onChange={(e) => setSickForm({ ...sickForm, actual_return: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Pay</Label>
              <Select value={sickForm.pay_type} onValueChange={(v) => setSickForm({ ...sickForm, pay_type: v })}>
                <SelectTrigger data-testid="sick-pay" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="statutory">Statutory sick pay</SelectItem>
                  <SelectItem value="company">Company paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label>Medically certified</Label><Switch data-testid="sick-certified" checked={sickForm.certified} onCheckedChange={(v) => setSickForm({ ...sickForm, certified: v })} /></div>
            <Button data-testid="sick-save" onClick={saveSick} className="w-full bg-slate-900 text-white">Log sickness</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lateOpen} onOpenChange={setLateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{lateEdit ? "Edit lateness" : "Log lateness"}</DialogTitle>
            <DialogDescription>A record only — this does not change pay or timesheets.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><Input data-testid="late-date" type="date" value={lateForm.date} onChange={(e) => setLateForm({ ...lateForm, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Minutes late</Label><Input data-testid="late-minutes" type="number" min="0" value={lateForm.minutes} onChange={(e) => setLateForm({ ...lateForm, minutes: e.target.value })} className="mt-1" /></div>
            <div><Label>Reason / note (optional)</Label><Textarea data-testid="late-note" value={lateForm.note} onChange={(e) => setLateForm({ ...lateForm, note: e.target.value })} className="mt-1" rows={2} /></div>
            <Button data-testid="late-save" onClick={saveLate} className="w-full bg-slate-900 text-white">{lateEdit ? "Save changes" : "Log lateness"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={balOpen} onOpenChange={setBalOpen}>
        <DialogContent className="max-w-sm" data-testid="edit-leave-balance-dialog">
          <DialogHeader><DialogTitle>Annual leave balance</DialogTitle><DialogDescription>Change the yearly entitlement and/or set exactly how many days are left. Setting days left records an adjustment so the history stays clear.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Entitlement (days)</Label><Input data-testid="bal-entitlement" type="number" step="0.5" min="0" value={balForm.entitlement} onChange={(e) => setBalForm({ ...balForm, entitlement: e.target.value })} className="mt-1" /></div>
              <div><Label>Leave left (days)</Label><Input data-testid="bal-remaining" type="number" step="0.5" value={balForm.remaining} onChange={(e) => setBalForm({ ...balForm, remaining: e.target.value })} className="mt-1" /></div>
            </div>
            <p className="text-[11px] text-zinc-500">Changing only the entitlement moves leave left by the same amount. To set an exact figure, change "Leave left" too.</p>
            <div><Label>Reason</Label><Textarea data-testid="bal-reason" rows={2} value={balForm.reason} onChange={(e) => setBalForm({ ...balForm, reason: e.target.value })} placeholder="e.g. Contract changed to 25 days" className="mt-1" /></div>
            <Button data-testid="bal-save" disabled={!balForm.reason.trim()} onClick={saveBal} className="w-full bg-slate-900 text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-sm" data-testid="adjust-leave-dialog">
          <DialogHeader><DialogTitle>Adjust leave taken</DialogTitle><DialogDescription>Use + to add days taken or - to remove them. Annual leave adjustments update the balance.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Leave type</Label>
              <Select value={adjForm.leave_type} onValueChange={(v) => setAdjForm({ ...adjForm, leave_type: v })}>
                <SelectTrigger data-testid="adj-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{leaveTypes.map((t) => <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Days (+/-)</Label><Input data-testid="adj-days" type="number" step="0.5" value={adjForm.days} onChange={(e) => setAdjForm({ ...adjForm, days: e.target.value })} placeholder="e.g. 2 or -1.5" className="mt-1" /></div>
              <div><Label>Date</Label><Input data-testid="adj-date" type="date" value={adjForm.date} onChange={(e) => setAdjForm({ ...adjForm, date: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Reason</Label><Textarea data-testid="adj-reason" rows={2} value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="e.g. Days taken before system go-live" className="mt-1" /></div>
            <Button data-testid="adj-save" onClick={saveAdj} className="w-full bg-slate-900 text-white">Save adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
